import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CheckIcon, ChevronRightIcon } from '@radix-ui/react-icons';

/**
 * Dropdown Menu component using Radix UI
 * @param {Object} props - Component props
 * @returns {JSX.Element} RadixDropdownMenu component
 */
const RadixDropdownMenu = ({ 
  trigger, 
  items,
  align = 'end',
  side = 'bottom',
  ...props 
}) => {
  return (
    <DropdownMenu.Root {...props}>
      <DropdownMenu.Trigger asChild>
        {trigger}
      </DropdownMenu.Trigger>
      
      <DropdownMenu.Portal>
        <DropdownMenu.Content 
          className="min-w-[220px] bg-white rounded-md shadow-lg p-1 z-50 border border-gray-200"
          align={align}
          side={side}
          sideOffset={5}
        >
          {items.map((item, index) => {
            if (item.type === 'separator') {
              return <DropdownMenu.Separator key={index} className="h-px bg-gray-200 my-1" />;
            }
            
            if (item.type === 'label') {
              return (
                <DropdownMenu.Label 
                  key={index} 
                  className="px-2 py-1.5 text-xs text-gray-500 font-medium"
                >
                  {item.label}
                </DropdownMenu.Label>
              );
            }
            
            if (item.type === 'submenu' && item.items) {
              return (
                <DropdownMenu.Sub key={index}>
                  <DropdownMenu.SubTrigger className="flex items-center justify-between px-2 py-1.5 text-sm rounded hover:bg-gray-100 cursor-default outline-none">
                    <span>{item.label}</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </DropdownMenu.SubTrigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.SubContent
                      className="min-w-[220px] bg-white rounded-md shadow-lg p-1 z-50 border border-gray-200"
                      sideOffset={2}
                      alignOffset={-5}
                    >
                      {item.items.map((subItem, subIndex) => (
                        <DropdownMenu.Item
                          key={subIndex}
                          className="flex items-center px-2 py-1.5 text-sm rounded hover:bg-gray-100 cursor-default outline-none"
                          disabled={subItem.disabled}
                          onSelect={subItem.onSelect}
                        >
                          {subItem.icon && <span className="mr-2">{subItem.icon}</span>}
                          <span>{subItem.label}</span>
                          {subItem.checked && (
                            <CheckIcon className="w-4 h-4 ml-auto" />
                          )}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.SubContent>
                  </DropdownMenu.Portal>
                </DropdownMenu.Sub>
              );
            }
            
            if (item.type === 'checkbox') {
              return (
                <DropdownMenu.CheckboxItem
                  key={index}
                  className="flex items-center px-2 py-1.5 text-sm rounded hover:bg-gray-100 cursor-default outline-none"
                  checked={item.checked}
                  onCheckedChange={item.onCheckedChange}
                  disabled={item.disabled}
                >
                  <DropdownMenu.ItemIndicator className="mr-2">
                    <CheckIcon className="w-4 h-4" />
                  </DropdownMenu.ItemIndicator>
                  {item.label}
                </DropdownMenu.CheckboxItem>
              );
            }
            
            // Default: regular item
            return (
              <DropdownMenu.Item
                key={index}
                className={`flex items-center px-2 py-1.5 text-sm rounded hover:bg-gray-100 cursor-default outline-none ${
                  item.destructive ? 'text-red-600' : ''
                } ${item.className || ''}`}
                disabled={item.disabled}
                onSelect={item.onClick || item.onSelect}
              >
                {item.icon && <span className="mr-2">{item.icon}</span>}
                <span>{item.label}</span>
                {item.shortcut && (
                  <span className="ml-auto text-xs text-gray-500">{item.shortcut}</span>
                )}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default RadixDropdownMenu;
